/**
 * Body Stats service layer.
 * Abstracts all Firestore operations for body stats tracking.
 * Stores stats as a subcollection under users: users/{userId}/bodyStats/{entryId}
 */
import {
  collection,
  doc,
  setDoc,
  getDocs,
  deleteDoc,
  query,
  orderBy,
  limit as firestoreLimit,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const USERS_COLLECTION = 'users';
const BODY_STATS_SUBCOLLECTION = 'bodyStats';

/**
 * Serialize a Firestore body stats document snapshot into a plain object.
 * @param {import('firebase/firestore').DocumentSnapshot} docSnap
 * @returns {Object}
 */
const serializeBodyStats = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    date: data.date instanceof Timestamp
      ? data.date.toDate().toISOString()
      : data.date || new Date().toISOString(),
    weight: data.weight ?? null,
    bodyFatPercentage: data.bodyFatPercentage ?? null,
    measurements: data.measurements || {
      chest: null,
      waist: null,
      hips: null,
      arms: null,
      thighs: null,
    },
    notes: data.notes || '',
    createdAt: data.createdAt instanceof Timestamp
      ? data.createdAt.toDate().toISOString()
      : data.createdAt || new Date().toISOString(),
  };
};

/**
 * Add a new body stats entry for a user.
 * @param {string} userId - The user's ID.
 * @param {Object} data - The body stats data.
 * @param {Date|string} data.date - The date of the measurement.
 * @param {number|null} [data.weight] - Weight value.
 * @param {number|null} [data.bodyFatPercentage] - Body fat percentage.
 * @param {Object} [data.measurements] - Body measurements.
 * @param {string} [data.notes] - Optional notes.
 * @returns {Promise<Object>} The created body stats entry.
 */
export const addBodyStats = async (userId, data) => {
  try {
    const statsRef = doc(
      collection(db, USERS_COLLECTION, userId, BODY_STATS_SUBCOLLECTION)
    );
    const entryId = statsRef.id;

    const dateValue = data.date instanceof Date
      ? Timestamp.fromDate(data.date)
      : Timestamp.fromDate(new Date(data.date));

    const entry = {
      date: dateValue,
      weight: data.weight ?? null,
      bodyFatPercentage: data.bodyFatPercentage ?? null,
      measurements: {
        chest: data.measurements?.chest ?? null,
        waist: data.measurements?.waist ?? null,
        hips: data.measurements?.hips ?? null,
        arms: data.measurements?.arms ?? null,
        thighs: data.measurements?.thighs ?? null,
      },
      notes: data.notes || '',
      createdAt: serverTimestamp(),
    };

    await setDoc(statsRef, entry);

    return {
      id: entryId,
      ...entry,
      date: (data.date instanceof Date ? data.date : new Date(data.date)).toISOString(),
      createdAt: new Date().toISOString(),
    };
  } catch (error) {
    handleServiceError(error, 'Add body stats', 'Failed to save body stats. Please try again.');
  }
};

/**
 * Get body stats entries for a user within a date range.
 * @param {string} userId - The user's ID.
 * @param {Date} startDate - Start date.
 * @param {Date} endDate - End date.
 * @returns {Promise<Array<Object>>} Array of body stats entries.
 */
export const getBodyStats = async (userId, startDate, endDate) => {
  try {
    const statsRef = collection(
      db, USERS_COLLECTION, userId, BODY_STATS_SUBCOLLECTION
    );
    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    const q = query(
      statsRef,
      where('date', '>=', startTimestamp),
      where('date', '<=', endTimestamp),
      orderBy('date', 'asc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(serializeBodyStats);
  } catch (error) {
    handleServiceError(error, 'Get body stats', 'Failed to load body stats. Please try again.');
  }
};

/**
 * Get the most recent body stats entry for a user.
 * @param {string} userId - The user's ID.
 * @returns {Promise<Object|null>} The latest body stats entry or null.
 */
export const getLatestBodyStats = async (userId) => {
  try {
    const statsRef = collection(
      db, USERS_COLLECTION, userId, BODY_STATS_SUBCOLLECTION
    );
    const q = query(
      statsRef,
      orderBy('date', 'desc'),
      firestoreLimit(1)
    );

    const querySnapshot = await getDocs(q);
    if (querySnapshot.empty) {
      return null;
    }

    return serializeBodyStats(querySnapshot.docs[0]);
  } catch (error) {
    handleServiceError(error, 'Get latest body stats', 'Failed to load latest body stats.');
  }
};

/**
 * Delete a body stats entry.
 * @param {string} userId - The user's ID.
 * @param {string} entryId - The body stats entry ID.
 * @returns {Promise<string>} The deleted entry ID.
 */
export const deleteBodyStats = async (userId, entryId) => {
  try {
    const docRef = doc(
      db, USERS_COLLECTION, userId, BODY_STATS_SUBCOLLECTION, entryId
    );
    await deleteDoc(docRef);
    return entryId;
  } catch (error) {
    handleServiceError(error, 'Delete body stats', 'Failed to delete body stats entry.');
  }
};
